<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9998] flex items-center justify-center p-3 overflow-hidden overscroll-none"
      @click.stop
      @wheel.prevent
      @touchmove.prevent
    >
      <button
        class="absolute inset-0 bg-black/50"
        aria-label="Fermer"
        @click.stop="closeProduct"
      ></button>
      <form
        class="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        @submit.prevent
        @click.stop
        @wheel.stop
        @touchmove.stop
      >
        <div class="p-5 border-b border-gray-200 flex justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-black">
              Compléter la fiche MEL
            </h2>
            <p class="text-sm text-gray-600">
              {{ modelValue.reference }} · {{ modelValue.name }}
            </p>
          </div>
          <button type="button" class="text-gray-500" @click="closeProduct">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div
          v-if="openingProduct"
          class="flex flex-col items-center justify-center gap-3 px-5 py-16 text-gray-600"
        >
          <i class="fas fa-spinner fa-spin text-2xl text-blue-600"></i>
          <p class="text-sm font-medium text-black">
            Chargement des photos par couleur…
          </p>
          <p class="text-xs text-gray-500 text-center max-w-sm">
            La fiche s’affiche dès que le détail Shopify est prêt.
          </p>
        </div>

        <template v-else>
        <div class="px-5 py-4 border-b border-gray-200 space-y-3">
          <p class="text-sm font-semibold text-black">Catégorie eFashion</p>
          <div
            v-if="pendingCategories.length && !hasEfashionCategorySelection"
            class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 space-y-2"
          >
            <p class="text-sm font-medium text-red-800">
              Catégorie Shopify
            </p>
            <p class="text-xs text-red-700">
              Vous pouvez lier «
              {{ pendingCategories[0] }}
              » à une catégorie eFashion (réutilisé ensuite), ou choisir
              directement une catégorie ci-dessous
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="pending in pendingCategories"
                :key="pending"
                type="button"
                class="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                @click="openPendingCategoryPicker(pending)"
              >
                <i class="fas fa-link text-[10px]"></i>
                Lier : {{ pending }}
              </button>
            </div>
          </div>
          <CategoryTreeAutocomplete
            :tree="categoriesTree"
            :cat-id="selectedCategoryLevel1"
            :sous-id="selectedCategoryLevel2"
            :sous-sous-id="selectedCategoryLevel3"
            label="Recherche rapide catégorie"
            placeholder="Rechercher une catégorie (ex. robe, jean…)"
            @select="onCategoryPathSelect"
            @clear="onCategoryPathClear"
          />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <BaseInput
              v-model="selectedCategoryLevel1"
              type="select"
              open-on-focus
              label="Catégorie"
              :options="categoryLevel1Options"
              placeholder="Choisir une catégorie"
              required
              @change="onCategoryLevel1Change"
            />
            <BaseInput
              v-model="selectedCategoryLevel2"
              type="select"
              open-on-focus
              label="Sous-catégorie"
              :options="categoryLevel2Options"
              placeholder="Choisir une sous-catégorie"
              :disabled="!selectedCategoryLevel1"
              required
              @change="onCategoryLevel2Change"
            />
            <BaseInput
              v-model="selectedCategoryLevel3"
              type="select"
              open-on-focus
              label="Sous-sous-catégorie"
              :options="categoryLevel3Options"
              placeholder="Choisir une ss-catégorie"
              :disabled="
                !selectedCategoryLevel2 || !categoryLevel3Options.length
              "
              @change="onCategoryLevel3Change"
            />
          </div>
          <p
            v-if="modelValue?.productType || modelValue?.shopifyCategoryName"
            class="text-xs text-gray-500"
          >
            Type produit Shopify :
            {{ modelValue.productType || modelValue.shopifyCategoryName }}
          </p>
        </div>
        <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <BaseInput
              v-model="form.brandId"
              type="select"
              open-on-focus
              label="Marque"
              :options="brandOptions"
              placeholder="Choisir une marque"
              :required="brandOptions.length > 1"
            />
            <p v-if="editedProduct?.pendingBrands?.length" class="text-xs text-amber-700 mt-1">
              Marque Shopify « {{ editedProduct.pendingBrands[0] }} » à lier dans Configuration Shopify,
              ou choisissez une marque pour cette fiche.
            </p>
          </div>
          <BaseInput
            v-model="form.collectionId"
            type="select"
            open-on-focus
            label="Collection"
            :options="collectionOptions"
            placeholder="Toutes les saisons"
            required
          />
          <div>
            <BaseInput
              v-model="form.provenanceId"
              type="select"
              open-on-focus
              label="Provenance"
              :options="provenanceOptions"
              placeholder="Choisir un pays"
              required
            />
          </div>
          <div>
            <BaseInput
              v-model="form.price"
              type="number"
              label="Prix HT"
              step="0.01"
              min="0"
              placeholder="0.00"
              required
            />
            <p class="text-xs text-amber-700 mt-1">
              Les prix Shopify sont souvent des prix B2C. Vérifiez le prix
              grossiste HT eFashion avant la mise en ligne.
            </p>
            <p
              v-if="productShopifyPriceHint"
              class="text-xs text-gray-500 mt-1"
            >
              Prix Shopify d'origine :
              {{ formatPrice(productShopifyPriceHint) }}
            </p>
            <p
              v-if="productWholesalePriceHint"
              class="text-xs text-gray-500 mt-1"
            >
              Prix grossiste détecté :
              {{ formatPrice(productWholesalePriceHint) }}
            </p>
          </div>
          <div>
            <BaseInput
              v-model="form.weight"
              type="number"
              label="Poids unitaire (kg)"
              step="0.001"
              min="0.001"
              :max="5"
              placeholder="0.000"
              required
            />
            <p class="text-xs text-red-600 mt-0.5">
              Merci de bien vouloir augmenter légèrement le poids pour prendre
              en compte les cintres, cartons ou surplus d'emballage. Maximum
              5 kg.
            </p>
            <p
              v-if="productShopifyWeightHint"
              class="text-xs text-gray-500 mt-1"
            >
              Poids Shopify d'origine :
              {{ formatWeight(productShopifyWeightHint) }}
            </p>
          </div>
          <div class="md:col-span-2">
            <CompositionForm
              v-model="selectedCompositions"
              :composition-type="compositionType"
              :localisations="compositionLocations"
              :compositions-list="shoeCompositions"
              :autocomplete-options="compositionAutocompleteOptions"
              :show-autocomplete-dropdown="showCompositionDropdown"
              :is-loading-autocomplete="isLoadingCompositionAutocomplete"
              @composition-input="handleCompositionInput"
              @hide-dropdown="hideCompositionDropdown"
              @select-autocomplete="selectComposition"
            />
            <p v-if="editedProduct?.compositionLabel" class="mt-2 text-xs text-gray-600">
              Composition Shopify : {{ editedProduct.compositionLabel }}
              <span v-if="editedProduct.pendingCompositions?.length" class="block text-amber-700 mt-1">
                Matières à valider dans Configuration Shopify :
                {{ editedProduct.pendingCompositions.join(", ") }}
              </span>
              <span v-if="editedProduct.compositionNeedsReview" class="block text-amber-700 mt-1">
                Composition Shopify à vérifier : format ou total des pourcentages invalide.
              </span>
            </p>
          </div>
          <BaseInput
            v-model="form.soldBy"
            type="select"
            open-on-focus
            label="Vendu par"
            :options="soldByOptions"
            placeholder="Tailles"
            required
          />
          <p class="md:col-span-2 -mt-2 text-xs text-gray-500">
            Par défaut : Tailles. Modifiable si besoin (Couleurs / Couleurs
            mélangées).
          </p>
          <BaseInput
            v-model="form.sizeRangeId"
            type="select"
            open-on-focus
            label="Taille du paquet"
            :options="sizeRangeOptions"
            placeholder="Choisir une déclinaison"
            required
          />
          <BaseInput
            v-if="form.soldBy && form.soldBy !== 'tailles'"
            v-model="form.packId"
            type="select"
            open-on-focus
            label="Quantité du paquet"
            :options="packOptions"
            placeholder="Choisir un pack"
            required
          />
          <BaseInput
            v-if="form.soldBy === 'melangees' && form.sizeRangeId"
            v-model="form.stock"
            type="number"
            label="Stock en paquet (optionnel)"
            min="0"
            placeholder="0"
          />
          <div class="md:col-span-2">
            <CouleursSelector
              v-model="selectedColors"
              :couleurs-list="officialColors"
              :linked-source-labels-by-color-id="linkedLabelsWithoutCollisions"
              :tailles-list="
                form.soldBy === 'melangees' ? selectedSizeNames : undefined
              "
            />
            <div
              v-if="colorCollisions.length"
              class="mt-3 space-y-2"
            >
              <p class="text-sm font-medium text-red-700">
                Plusieurs couleurs Shopify sont liées à la même couleur officielle
              </p>
              <div
                v-for="group in colorCollisions"
                :key="group.colorId"
                class="space-y-2"
              >
                <p class="text-xs text-red-700">
                  Liées à {{ group.officialLabel }}
                </p>
                <div
                  v-for="name in group.names"
                  :key="`${group.colorId}-${name}`"
                  class="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2"
                >
                  <span class="text-sm font-semibold text-red-700">{{ name }}</span>
                  <button
                    type="button"
                    class="text-sm text-red-700 underline hover:text-red-900"
                    @click="openCollisionColorPicker(name)"
                  >
                    {{
                      collisionChoiceLabel(name)
                        ? `Liée à ${collisionChoiceLabel(name)} pour cette fiche`
                        : "Choisir une couleur officielle"
                    }}
                  </button>
                </div>
              </div>
            </div>
            <div
              v-if="pendingColors.length"
              class="mt-3 space-y-2"
            >
              <p class="text-sm font-medium text-red-700">
                Couleurs Shopify à lier
              </p>
              <div
                v-for="pending in pendingColors"
                :key="pending"
                class="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2"
              >
                <span class="text-sm font-semibold text-red-700">{{
                  pending
                }}</span>
                <button
                  type="button"
                  class="text-sm text-red-700 underline hover:text-red-900"
                  @click="openPendingColorPicker(pending)"
                >
                  Veuillez choisir une couleur officielle
                </button>
              </div>
            </div>
            <div
              v-if="form.soldBy === 'melangees'"
              class="mt-2 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r"
            >
              <p v-if="!form.sizeRangeId" class="text-sm text-blue-700">
                Sélectionnez la déclinaison pour saisir les quantités par
                taille.
              </p>
              <p
                v-else-if="melangeesPackMismatchMessage"
                class="text-sm text-red-600"
              >
                {{ melangeesPackMismatchMessage }}
              </p>
              <p v-else class="text-xs text-blue-600">
                Pour chaque couleur, indiquez la composition du paquet par
                taille (ex. 1 en S, 2 en M, 1 en L). La somme des couleurs doit
                correspondre à la quantité du pack.
              </p>
            </div>
          </div>
          <div
            v-if="
              form.soldBy === 'tailles' &&
              selectedColors.length > 0 &&
              selectedSizeNames.length > 0
            "
            class="md:col-span-2 space-y-3"
          >
            <div>
              <p class="text-sm font-medium text-gray-800">Stock par taille</p>
              <p class="text-xs text-gray-500 mt-0.5">
                Quantités disponibles par couleur et taille (préremplies depuis
                Shopify si possible).
              </p>
            </div>
            <div
              v-for="color in selectedColors"
              :key="`stock-${color.id}`"
              class="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
            >
              <div class="flex items-center gap-2 flex-shrink-0 w-28">
                <span
                  class="inline-block w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                  :style="{
                    backgroundColor: color.hexColor || '#CCCCCC',
                  }"
                ></span>
                <span class="text-sm font-medium text-black truncate">{{
                  color.nom
                }}</span>
              </div>
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="(ts, idx) in color.tailleStocks || []"
                  :key="`${color.id}-${ts.taille}`"
                  class="flex items-center gap-1"
                >
                  <span class="text-xs text-gray-600">{{ ts.taille }}</span>
                  <input
                    :value="ts.quantity != null ? String(ts.quantity) : ''"
                    type="number"
                    min="0"
                    class="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
                    placeholder="0"
                    @input="
                      (e) =>
                        setTailleStockQuantity(
                          color.id,
                          idx,
                          (e.target as HTMLInputElement).value
                        )
                    "
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="form.soldBy === 'couleurs' && selectedColors.length > 0"
            class="md:col-span-2 space-y-3"
          >
            <div>
              <p class="text-sm font-medium text-gray-800">Stock par couleur</p>
              <p class="text-xs text-gray-500 mt-0.5">
                Quantités disponibles (préremplies depuis Shopify si possible).
              </p>
            </div>
            <div
              v-for="color in selectedColors"
              :key="`color-stock-${color.id}`"
              class="p-3 border border-gray-200 rounded-md bg-gray-50 flex items-center gap-3"
            >
              <span
                class="inline-block w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                :style="{ backgroundColor: color.hexColor || '#CCCCCC' }"
              ></span>
              <span class="text-sm font-medium text-black w-28 truncate">{{
                color.nom
              }}</span>
              <input
                :value="color.stock != null ? String(color.stock) : ''"
                type="number"
                min="0"
                class="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
                placeholder="0"
                @input="
                  (e) =>
                    setColorStock(
                      color.id,
                      (e.target as HTMLInputElement).value
                    )
                "
              />
            </div>
          </div>
        </div>

        <div class="px-5 py-4 border-b border-gray-200">
          <ShopifyMelPhotosEditor
            v-if="modelValue"
            :key="`shopify-photos-${modelValue.id}`"
            :product-gid="modelValue.id"
            :source-images="modelValue.images"
            :saved-images="productImageOverrides"
            :selected-colors="selectedColors"
            :color-collisions="colorCollisions"
            :color-source-choices="colorSourceChoices"
            :linked-source-labels-by-color-id="linkedLabelsWithoutCollisions"
            @update:images="onProductImagesUpdate"
            @saved="onProductImagesSaved"
            @error="emitError"
          />
        </div>

        <div class="p-5 border-t border-gray-200 space-y-3">
          <p v-if="!canSaveProductForm" class="text-sm text-red-600">
            Champs obligatoires manquants :
            {{ productFormMissingFields.join(", ") }}
          </p>
          <div class="flex justify-end gap-2">
            <ActionButton
              label="Annuler"
              variant="secondary"
              type="button"
              @click="closeProduct"
            />
            <ActionButton
              label="Enregistrer"
              variant="primary"
              type="button"
              :loading="savingProduct"
              :disabled="savingProduct || !canSaveProductForm"
              @click="saveProduct"
            />
          </div>
        </div>
        </template>
      </form>
    </div>
  </Teleport>

  <CouleursPickerModal
    v-model:isVisible="pendingColorModalVisible"
    :couleursList="officialColors"
    :official-only="true"
    :selectedIds="collisionPickerSelectedIds"
    :single="Boolean(collisionColorToAssign)"
    :disabled-ids="collisionDisabledColorIds"
    :linked-source-labels-by-color-id="linkedLabelsWithoutCollisions"
    @confirm="onPendingColorConfirm"
  />

  <Teleport to="body">
    <div
      v-if="pendingCategoryModalVisible"
      class="fixed inset-0 z-[10050] flex items-center justify-center p-3 overflow-hidden overscroll-none"
      @click.stop
    >
      <button
        class="absolute inset-0 bg-black/50"
        aria-label="Fermer"
        @click="pendingCategoryModalVisible = false"
      ></button>
      <div
        class="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4"
        @click.stop
      >
        <h3 class="text-base font-semibold text-black">
          Lier la catégorie Shopify
        </h3>
        <p class="text-sm text-gray-600">
          «
          <span class="font-medium text-black">{{
            pendingCategoryToLink
          }}</span>
          » → catégorie feuille eFashion
        </p>
        <CategoryTreeAutocomplete
          :tree="categoriesTree"
          cat-id=""
          sous-id=""
          sous-sous-id=""
          label="Recherche catégorie"
          placeholder="Rechercher une catégorie feuille…"
          @select="onPendingCategorySelect"
        />
        <div class="flex justify-end">
          <ActionButton
            label="Annuler"
            variant="secondary"
            size="sm"
            @click="pendingCategoryModalVisible = false"
          />
        </div>
      </div>
    </div>
  </Teleport>

</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import BaseInput from "@/components/forms/BaseInput.vue";
import CategoryTreeAutocomplete from "@/components/forms/CategoryTreeAutocomplete.vue";
import ShopifyMelPhotosEditor from "@/components/shopify/ShopifyMelPhotosEditor.vue";
import CouleursSelector from "@/components/mel/CouleursSelector.vue";
import CouleursPickerModal from "@/components/mel/CouleursPickerModal.vue";
import CompositionForm from "@/components/mel/CompositionForm.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import { useShopifyMelProductSheet } from "@/composables/useShopifyMelProductSheet";
import type { CouleurAPI } from "@/types/mel-components";
import type { CategoryPathSelection } from "@/types/categorie";
import type { ShopifyCatalogPage, ShopifyProduct } from "@/types/shopify";

const props = defineProps<{
  modelValue: ShopifyProduct | null;
  reloadCatalog: () => Promise<void>;
}>();

const catalog = defineModel<ShopifyCatalogPage>("catalog", { required: true });

const emit = defineEmits<{
  (event: "update:modelValue", value: ShopifyProduct | null): void;
  (event: "saved"): void;
  (event: "error", message: string): void;
  (event: "success", message: string): void;
}>();

const sheet = useShopifyMelProductSheet({
  catalog,
  onReloadCatalog: async () => {
    await props.reloadCatalog();
    emit("saved");
  },
  success: (message) => emit("success", message),
  error: (message) => emit("error", message),
});

const {
  editedProduct,
  savingProduct,
  openingProduct,
  productImageOverrides,
  categoriesTree,
  selectedColors,
  pendingColors,
  colorCollisions,
  colorSourceChoices,
  collisionChoiceLabel,
  assignCollisionColor,
  pendingCategories,
  hasEfashionCategorySelection,
  selectedCompositions,
  compositionType,
  compositionLocations,
  shoeCompositions,
  compositionAutocompleteOptions,
  showCompositionDropdown,
  isLoadingCompositionAutocomplete,
  selectedCategoryLevel1,
  selectedCategoryLevel2,
  selectedCategoryLevel3,
  productShopifyPriceHint,
  productWholesalePriceHint,
  productShopifyWeightHint,
  form,
  brandOptions,
  collectionOptions,
  provenanceOptions,
  packOptions,
  sizeRangeOptions,
  categoryLevel1Options,
  categoryLevel2Options,
  categoryLevel3Options,
  selectedSizeNames,
  soldByOptions,
  melangeesPackMismatchMessage,
  productFormMissingFields,
  canSaveProductForm,
  officialColors,
  linkedSourceLabelsByColorId,
  openProduct,
  closeProduct,
  saveProduct,
  linkPendingColor,
  linkPendingCategory,
  onCategoryPathSelect,
  onCategoryLevel1Change,
  onCategoryLevel2Change,
  onCategoryLevel3Change,
  onCategoryPathClear,
  handleCompositionInput,
  selectComposition,
  hideCompositionDropdown,
  onProductImagesUpdate,
  onProductImagesSaved,
  setTailleStockQuantity,
  setColorStock,
} = sheet;

function collisionLabelKey(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const linkedLabelsWithoutCollisions = computed(() => {
  const blocked = new Set(
    colorCollisions.value.flatMap((group) =>
      group.names.map((name) => collisionLabelKey(name)),
    ),
  );
  const next: Record<number, string[]> = {};
  for (const [id, labels] of Object.entries(linkedSourceLabelsByColorId.value)) {
    const kept = labels.filter(
      (label) => !blocked.has(collisionLabelKey(label)),
    );
    if (kept.length) next[Number(id)] = kept;
  }
  return next;
});

const collisionPickerSelectedIds = computed(() => {
  const name = collisionColorToAssign.value;
  if (!name) return [];
  const key = collisionLabelKey(name);
  const choice = colorSourceChoices.value.find(
    (item) => collisionLabelKey(item.sourceName) === key,
  );
  return choice ? [choice.colorId] : [];
});

const collisionDisabledColorIds = computed(() => {
  const name = collisionColorToAssign.value;
  if (!name) return [];
  const key = collisionLabelKey(name);
  const current = colorSourceChoices.value.find(
    (item) => collisionLabelKey(item.sourceName) === key,
  );
  const collisionIds = new Set(
    colorCollisions.value.map((group) => group.colorId),
  );
  const takenByAnotherName = new Set(
    colorSourceChoices.value
      .filter((item) => collisionLabelKey(item.sourceName) !== key)
      .map((item) => item.colorId),
  );
  const disabled = new Set<number>();
  for (const colorId of takenByAnotherName) disabled.add(colorId);
  for (const color of selectedColors.value) {
    const colorId = Number(color.id);
    if (current && colorId === current.colorId) continue;
    if (collisionIds.has(colorId) && !takenByAnotherName.has(colorId)) continue;
    disabled.add(colorId);
  }
  return [...disabled];
});

const pendingColorModalVisible = ref(false);
const pendingColorToLink = ref<string | null>(null);
const collisionColorToAssign = ref<string | null>(null);
const pendingCategoryModalVisible = ref(false);
const pendingCategoryToLink = ref<string | null>(null);

function openPendingColorPicker(sourceLabel: string) {
  collisionColorToAssign.value = null;
  pendingColorToLink.value = sourceLabel;
  pendingColorModalVisible.value = true;
}

function openCollisionColorPicker(sourceLabel: string) {
  pendingColorToLink.value = null;
  collisionColorToAssign.value = sourceLabel;
  pendingColorModalVisible.value = true;
}

function openPendingCategoryPicker(sourceLabel: string) {
  pendingCategoryToLink.value = sourceLabel;
  pendingCategoryModalVisible.value = true;
}

async function onPendingColorConfirm(colors: CouleurAPI[]) {
  const collisionLabel = collisionColorToAssign.value;
  const sourceLabel = pendingColorToLink.value;
  const official = colors[0];
  pendingColorModalVisible.value = false;
  pendingColorToLink.value = null;
  collisionColorToAssign.value = null;
  if (!official) return;
  if (collisionLabel) {
    assignCollisionColor(collisionLabel, official);
    return;
  }
  if (!sourceLabel) return;
  await linkPendingColor(sourceLabel, official);
}

async function onPendingCategorySelect(path: CategoryPathSelection) {
  const sourceLabel = pendingCategoryToLink.value;
  pendingCategoryModalVisible.value = false;
  pendingCategoryToLink.value = null;
  if (!sourceLabel) return;
  await linkPendingCategory(sourceLabel, path);
}

function formatPrice(value: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

function formatWeight(value: number) {
  return `${value.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })} kg`;
}

function emitError(message: string) {
  emit("error", message);
}

watch(
  () => props.modelValue,
  (product) => {
    if (product && product.id !== editedProduct.value?.id) {
      void openProduct(product);
    }
    if (!product && editedProduct.value) {
      closeProduct();
    }
  },
  { immediate: true },
);

watch(editedProduct, (product) => {
  if (product?.id !== props.modelValue?.id) {
    emit("update:modelValue", product);
  }
});
</script>
